import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from './useAuth';

interface LocationContextValue {
  /** The currently selected location ID */
  activeLocationId: string | undefined;
  /** The currently selected location name */
  activeLocationName: string | undefined;
  /** The active role within the current location (for users with multiple roles at same location) */
  activeLocationRole: string | undefined;
  /** Whether the user is currently scoped to a location */
  isInLocation: boolean;
  /** Whether the user CAN navigate between locations/roles */
  canSwitchLocations: boolean;
  /** Navigate into a location with a specific role */
  enterLocation: (locationId: string, locationName: string, role?: string) => void;
  /** Go back to org level (or top level for super admin) */
  exitLocation: () => void;
  /**
   * Effective location ID for API calls.
   * - For location-level roles: always their location from membership
   * - For org-level roles: the selected location, or undefined if at org level
   */
  effectiveLocationId: string | undefined;

  // Org-level scoping (for SUPER_ADMIN)
  /** The currently selected organization ID (SUPER_ADMIN navigating into an org) */
  activeOrgId: string | undefined;
  /** The currently selected organization name */
  activeOrgName: string | undefined;
  /** Whether the user is currently scoped to an org (SUPER_ADMIN inside an org) */
  isInOrg: boolean;
  /** Navigate into an organization (SUPER_ADMIN) */
  enterOrg: (orgId: string, orgName: string) => void;
  /** Go back to top level (SUPER_ADMIN) */
  exitOrg: () => void;
  /**
   * Effective organization ID for API calls.
   * - For ORG_ADMIN: always their org from membership
   * - For SUPER_ADMIN: the selected org, or undefined if at top level
   */
  effectiveOrgId: string | undefined;

  /**
   * URL prefix for building hierarchical links.
   * e.g. "/org/abc123/loc/def456" or "/loc/def456" or ""
   */
  urlPrefix: string;
}

const LocationContext = createContext<LocationContextValue | undefined>(undefined);

/**
 * Parse org and location IDs from the current URL path.
 * Supports: /org/:orgId/..., /loc/:locId/..., /org/:orgId/loc/:locId/...
 */
function parseUrlContext(pathname: string): { urlOrgId?: string; urlLocId?: string } {
  const parts = pathname.split('/');
  const orgIdx = parts.indexOf('org');
  const locIdx = parts.indexOf('loc');
  return {
    urlOrgId: orgIdx >= 0 && orgIdx + 1 < parts.length ? parts[orgIdx + 1] : undefined,
    urlLocId: locIdx >= 0 && locIdx + 1 < parts.length ? parts[locIdx + 1] : undefined,
  };
}

export const LocationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const {
    activeLocationId: membershipLocationId,
    activeLocationName: membershipLocationName,
    activeOrganizationId: membershipOrgId,
    activeOrganizationName: membershipOrgName,
    isOrgLevel, isSuperAdmin, user,
  } = useAuth();

  const routerLocation = useLocation();
  const navigate = useNavigate();

  // Derive org/location IDs from URL
  const { urlOrgId, urlLocId } = parseUrlContext(routerLocation.pathname);

  // Name cache: since URL only carries IDs, we cache names when user navigates
  const [nameCache, setNameCache] = useState<Record<string, string>>({});

  const cachedLocationName = urlLocId ? nameCache[`loc:${urlLocId}`] : undefined;
  const cachedOrgName = urlOrgId ? nameCache[`org:${urlOrgId}`] : undefined;

  // For multi-location LOCATION_ADMIN: track selected location role
  const [selectedLocationRole, setSelectedLocationRole] = useState<string | undefined>();

  // Get unique location+role combinations (handles same location with different roles)
  const locationMemberships = useMemo(() => {
    if (!user) return [];
    return user.memberships.filter(m => m.locationId);
  }, [user]);

  // Get unique org memberships (for multi-org org admins)
  const orgMemberships = useMemo(() => {
    if (!user) return [];
    return user.memberships.filter(m => m.organizationId && !m.locationId && m.role !== 'SUPER_ADMIN');
  }, [user]);

  // User can switch if they have multiple distinct location+role combos
  const hasMultipleLocations = locationMemberships.length > 1;
  const hasMultipleOrgs = orgMemberships.length > 1;

  // Build URL prefix from current URL context
  const urlPrefix = useMemo(() => {
    let prefix = '';
    if (urlOrgId) prefix += `/org/${urlOrgId}`;
    if (urlLocId) prefix += `/loc/${urlLocId}`;
    return prefix;
  }, [urlOrgId, urlLocId]);

  const enterLocation = useCallback((locationId: string, locationName: string, role?: string) => {
    // Cache the name for display
    setNameCache(prev => ({ ...prev, [`loc:${locationId}`]: locationName }));
    if (role) setSelectedLocationRole(role);

    // For multi-location LOCATION_ADMIN: stay flat, no URL prefix
    if (hasMultipleLocations && !isOrgLevel && !isSuperAdmin) {
      // Just update the role state; flat URLs
      return;
    }

    // Build hierarchical URL
    if (urlOrgId) {
      navigate(`/org/${urlOrgId}/loc/${locationId}/dashboard`);
    } else if (isOrgLevel || isSuperAdmin) {
      navigate(`/loc/${locationId}/dashboard`);
    }
  }, [urlOrgId, isOrgLevel, isSuperAdmin, hasMultipleLocations, navigate]);

  const exitLocation = useCallback(() => {
    setSelectedLocationRole(undefined);
    if (urlOrgId) {
      navigate(`/org/${urlOrgId}/dashboard`);
    } else {
      navigate('/dashboard');
    }
  }, [urlOrgId, navigate]);

  const enterOrg = useCallback((orgId: string, orgName: string) => {
    setNameCache(prev => ({ ...prev, [`org:${orgId}`]: orgName }));
    navigate(`/org/${orgId}/dashboard`);
  }, [navigate]);

  const exitOrg = useCallback(() => {
    navigate('/dashboard');
  }, [navigate]);

  const value = useMemo<LocationContextValue>(() => {
    if (isSuperAdmin) {
      // SUPER_ADMIN: can navigate org -> location via URL
      return {
        activeLocationId: urlLocId,
        activeLocationName: cachedLocationName,
        activeLocationRole: selectedLocationRole,
        isInLocation: !!urlLocId,
        canSwitchLocations: true,
        enterLocation,
        exitLocation,
        effectiveLocationId: urlLocId,

        activeOrgId: urlOrgId,
        activeOrgName: cachedOrgName,
        isInOrg: !!urlOrgId,
        enterOrg,
        exitOrg,
        effectiveOrgId: urlOrgId,

        urlPrefix,
      };
    }

    if (isOrgLevel) {
      // ORG_ADMIN: org is determined by active membership (switchable via role picker)
      return {
        activeLocationId: urlLocId,
        activeLocationName: cachedLocationName,
        activeLocationRole: selectedLocationRole,
        isInLocation: !!urlLocId,
        canSwitchLocations: true,
        enterLocation,
        exitLocation,
        effectiveLocationId: urlLocId,

        activeOrgId: membershipOrgId,
        activeOrgName: membershipOrgName,
        isInOrg: true,
        enterOrg: () => {},
        exitOrg: () => {},
        effectiveOrgId: membershipOrgId,

        urlPrefix,
      };
    }

    if (hasMultipleLocations) {
      // LOCATION_ADMIN with multiple locations - flat URLs, switch via picker
      const effectiveId = selectedLocationRole
        ? locationMemberships.find(m => String(m.role) === selectedLocationRole)?.locationId || membershipLocationId
        : membershipLocationId;
      const effectiveName = effectiveId !== membershipLocationId
        ? nameCache[`loc:${effectiveId}`] || membershipLocationName
        : membershipLocationName;
      const effectiveRole = selectedLocationRole || String(locationMemberships.find(m => m.locationId === effectiveId)?.role || '');
      return {
        activeLocationId: effectiveId,
        activeLocationName: effectiveName,
        activeLocationRole: effectiveRole,
        isInLocation: true,
        canSwitchLocations: true,
        enterLocation,
        exitLocation: () => {},
        effectiveLocationId: effectiveId,

        activeOrgId: membershipOrgId,
        activeOrgName: membershipOrgName,
        isInOrg: true,
        enterOrg: () => {},
        exitOrg: () => {},
        effectiveOrgId: membershipOrgId,

        urlPrefix: '', // flat URLs for multi-location
      };
    }

    // Single location role (PARENT, STAFF, single LOCATION_ADMIN)
    return {
      activeLocationId: membershipLocationId,
      activeLocationName: membershipLocationName,
      activeLocationRole: String(locationMemberships[0]?.role || ''),
      isInLocation: true,
      canSwitchLocations: false,
      enterLocation: () => {},
      exitLocation: () => {},
      effectiveLocationId: membershipLocationId,

      activeOrgId: membershipOrgId,
      activeOrgName: membershipOrgName,
      isInOrg: true,
      enterOrg: () => {},
      exitOrg: () => {},
      effectiveOrgId: membershipOrgId,

      urlPrefix: '', // flat URLs for single location
    };
  }, [isSuperAdmin, isOrgLevel, hasMultipleLocations, hasMultipleOrgs, urlLocId, urlOrgId, cachedLocationName, cachedOrgName, selectedLocationRole, membershipLocationId, membershipLocationName, membershipOrgId, membershipOrgName, enterLocation, exitLocation, enterOrg, exitOrg, urlPrefix, locationMemberships, nameCache]);

  return (
    <LocationContext.Provider value={value}>
      {children}
    </LocationContext.Provider>
  );
};

export function useLocationContext(): LocationContextValue {
  const context = useContext(LocationContext);
  if (!context) {
    throw new Error('useLocationContext must be used within a LocationProvider');
  }
  return context;
}
